import React, { useEffect, useState } from 'react';
import { FiX, FiTrash2 } from 'react-icons/fi';
import { userService } from '../../services/userService';
import toast from 'react-hot-toast';
import CurrencyInput from 'react-currency-input-field';

const UserGoalModal = ({ user, goal, onClose, onSuccess }) => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ 
    year: goal ? goal.year.toString() : new Date().getFullYear().toString(), 
    customer_target: goal ? goal.customer_target.toString() : '', 
    profitability_target: goal ? goal.profitability_target.toString() : '', 
    profitability_currency: goal ? goal.profitability_currency : 'TRY', 
    description: goal ? goal.description : '' 
  });
  const [saving, setSaving] = useState(false);

  const loadGoals = async () => {
    try {
      setLoading(true);
      const res = await userService.getUserGoals(user.id);
      setGoals(res.goals || []);
    } catch (err) {
      toast.error('Hedefler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadGoals(); }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.year || form.customer_target === '' || form.profitability_target === '') {
      toast.error('Yıl, müşteri hedefi ve kârlılık hedefi zorunludur');
      return;
    }
    
    try {
      setSaving(true);
      
      if (goal) {
        // Düzenleme modu
        await userService.updateUserGoal(goal.id, {
          year: form.year,
          customer_target: Number(form.customer_target) || 0,
          profitability_target: parseFloat(form.profitability_target) || 0,
          profitability_currency: form.profitability_currency,
          description: form.description || ''
        });
        toast.success('Hedef güncellendi');
        if (onSuccess) onSuccess(); // Düzenleme modunda onSuccess çağır
      } else {
        // Ekleme modu
        await userService.addUserGoal({
          year: form.year,
          customer_target: Number(form.customer_target) || 0,
          profitability_target: parseFloat(form.profitability_target) || 0,
          profitability_currency: form.profitability_currency,
          user_id: user.id,
          description: form.description || ''
        });
        toast.success('Hedef eklendi');
      }
      
      setForm({ year: new Date().getFullYear().toString(), customer_target: '', profitability_target: '', profitability_currency: 'TRY', description: '' });
      await loadGoals();
    } catch (err) {
      toast.error(goal ? 'Hedef güncellenemedi' : 'Hedef eklenemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (goalId) => {
    if (!window.confirm('Bu hedefi silmek istiyor musunuz?')) return;
    try {
      await userService.deleteUserGoal(goalId);
      toast.success('Hedef silindi');
      await loadGoals();
    } catch (err) {
      toast.error('Hedef silinemedi');
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-2xl">
        <div className="modal-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">
              {goal ? 'Hedef Düzenle' : 'Hedef Yönetimi'} - {user.user_fullname}
            </h3>
            <button onClick={onClose} className="text-white hover:text-gray-300"><FiX size={20} /></button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Ekleme formu */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Yıl</label>
              <input 
                type="number" 
                min="2000" 
                max="2100" 
                value={form.year} 
                onChange={(e) => handleChange('year', e.target.value)} 
                className="input-field w-full" 
                placeholder="2025" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Müşteri Hedefi</label>
              <input 
                type="number" 
                min="0" 
                value={form.customer_target} 
                onChange={(e) => handleChange('customer_target', e.target.value)} 
                className="input-field w-full" 
                placeholder="0" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kârlılık Hedefi</label>
              <CurrencyInput
                id="profitability_target"
                name="profitability_target"
                placeholder="0.00"
                value={form.profitability_target}
                onValueChange={(value, name) => {
                  handleChange('profitability_target', value || '');
                }}
                prefix=""
                suffix=""
                decimalsLimit={2}
                decimalSeparator="."
                groupSeparator=","
                allowDecimals={true}
                allowNegativeValue={false}
                className="input-field w-full"
                style={{
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  lineHeight: 'inherit'
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Para Birimi</label>
              <select 
                value={form.profitability_currency} 
                onChange={(e) => handleChange('profitability_currency', e.target.value)} 
                className="input-field w-full"
              >
                <option value="TRY">TL</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Açıklama</label>
              <input type="text" value={form.description} onChange={(e)=>handleChange('description', e.target.value)} className="input-field w-full" placeholder="Opsiyonel" />
            </div>
          </div>
          <div className="flex items-center justify-end">
            <button onClick={handleSave} disabled={saving} className="btn-primary">
              {saving ? 'Kaydediliyor...' : (goal ? 'Güncelle' : 'Ekle')}
            </button>
          </div>

          {/* Liste - sadece ekleme modunda göster */}
          {!goal && (
            <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Yıl</th>
                  <th>Müşteri Hedefi</th>
                  <th>Kârlılık Hedefi</th>
                  <th>Para Birimi</th>
                  <th>Açıklama</th>
                  <th className="text-right w-24">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="text-center py-6">Yükleniyor...</td></tr>
                ) : goals.length > 0 ? (
                  goals.map(goal => (
                    <tr key={goal.id}>
                      <td>{goal.year}</td>
                      <td>{goal.customer_target}</td>
                      <td>{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: goal.profitability_currency || 'TRY' }).format(Number(goal.profitability_target || 0))}</td>
                      <td>{goal.profitability_currency || 'TRY'}</td>
                      <td>{goal.description || '-'}</td>
                      <td className="text-right">
                        <button onClick={()=>handleDelete(goal.id)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" title="Sil">
                          <FiTrash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="6" className="text-center py-6 text-gray-500 dark:text-gray-400">Hedef bulunamadı</td></tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserGoalModal;


